import { createSlice, DeepPartial, PayloadAction } from "@reduxjs/toolkit"
import slugify from "slugify"

import { STATES_MAPPER } from "configs/country-state-names"
import { NO_OF_PROERTIES_PER_PAGE_IN_LISTING_PAGE } from "constant"
import { RootState } from "stores/store"
import { markStates } from "utils/misc"
import { slugifyForLocationName, normalizeText } from "utils/url"

import { SearchApiRequestType, SearchInitialState } from "./types"

const initialState: SearchInitialState = {
  state: {
    isSearchingInMap: false,
    isBoundaryVisible: true,
    page: 0,
    limit: NO_OF_PROERTIES_PER_PAGE_IN_LISTING_PAGE,
    shortName: "",
    placeId: null,
    bounds: null,
    lat: null,
    lng: null,
    placeType: "",
    longName: null,
    startDate: null,
    endDate: null,
    noOfDays: 0,
    adultCount: 2,
    childrenCount: 0,
    petAllowed: false,
    numberOfBedroom: 0,
    minBed: 0,
    numberOfBathroom: 0,
    smokeAllowed: false,
    childAllowed: false,
    eventAllowed: false,
    suitableChild: false,
    suitableInfant: false,
    currentUpdatedURL: "/",
    sortBy: "highToLow",
    toggleOn: true,
    propertyConfigTypeId: null,

    numberOfGuest: 0,
    maxprice: 0,
    minPrice: 0,

    FeaturesAndAmenities: [],
    Location: [],
    propertyTypeDesc: [],

    nearByProperties: false,
    isBoundaryAutoRemoved: false,
    apiRequestType: "",

    cancellable: false,

    showClearFilterButton: false, // show clear filter button in search page
    clickClearFilter: false, // clear filter button clicked or not
    allowSameDay: false, // allow same day check-in or not
    expandedMiles: null,
  },
  searchQuery: null,
}

export const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    resetSearch: (state) => {
      markStates(initialState, state)
    },
    markSearch: (
      state,
      action: PayloadAction<
        DeepPartial<SearchInitialState["state"]> & { shouldUpdateUrl?: boolean }
      >,
    ) => {
      markStates(action.payload, state.state)

      let slugName = state?.state.shortName || state?.state.longName || "USA" // default should be USA
      const tempArr = slugName?.split(",")
      if (tempArr?.length === 3) {
        // ["city","state","country"]
        tempArr[1] = ` ${
          (STATES_MAPPER[tempArr[1]?.trim()] as string) || tempArr[1]?.trim()
        }`
        slugName = tempArr?.join()
      }

      const query: Record<string, string> = {}

      const FeaturesAndAmenities =
        state?.state.FeaturesAndAmenities?.join(",") || ""
      if (FeaturesAndAmenities)
        query.FeaturesAndAmenities = FeaturesAndAmenities

      const Location = state?.state.Location?.join(",") || ""
      if (Location) query.Location = Location

      const propertyTypeDesc = state?.state.propertyTypeDesc?.join(",") || ""
      if (propertyTypeDesc) query.propertyTypeDesc = propertyTypeDesc

      const adultCount = state?.state.adultCount
      if (adultCount) query.adultCount = adultCount?.toString()

      const bounds = state?.state.bounds || ""
      if (bounds) query.bounds = bounds

      const childAllowed =
        state?.state.childAllowed ||
        (!!state?.state.childrenCount && state?.state.childrenCount > 0)
      if (childAllowed) query.childAllowed = "true"

      const childrenCount = state?.state.childrenCount
      if (childrenCount) query.childrenCount = childrenCount?.toString()

      const endDate = state?.state.endDate || ""
      if (endDate) query.endDate = endDate

      const eventAllowed = state?.state?.eventAllowed
      if (eventAllowed) query.eventAllowed = "true"

      const isSearchingInMap = state?.state.isSearchingInMap
      if (isSearchingInMap) query.isSearchingInMap = "true"

      const lat = state?.state.lat?.toString()
      if (lat && lat !== "32.7749295") query.lat = lat

      const lng = state?.state.lng?.toString()
      if (lng && lng !== "-122.4194155") query.lng = lng

      const maxprice = state?.state?.maxprice
      if (maxprice) query.maxprice = maxprice?.toString()

      const minBathRoom = state?.state?.numberOfBathroom
      if (minBathRoom) query.minBathRoom = minBathRoom?.toString()

      const minBedRoom = state?.state?.numberOfBedroom
      if (minBedRoom) query.minBedRoom = minBedRoom?.toString()

      const minBed = state?.state?.minBed
      if (minBed) query.minBed = minBed?.toString()

      const minPrice = state?.state?.minPrice
      if (minPrice) query.minPrice = minPrice?.toString()

      const name = encodeURIComponent(
        state?.state.shortName || state?.state.longName || "",
      )
      if (name) query.name = name

      const noOfDays = state?.state.noOfDays
      if (noOfDays) query.noOfDays = noOfDays?.toString()

      const numberOfGuest = state?.state?.numberOfGuest
      if (numberOfGuest) query.numberOfGuest = numberOfGuest?.toString()

      const page = state?.state.page
      if (page) query.page = page?.toString()

      const petAllowed = state?.state.petAllowed
      if (petAllowed) query.petAllowed = "true"

      const placeDesc = slugifyForLocationName(state?.state.longName || "")
      if (placeDesc) query.placeDesc = placeDesc

      const placeId = state?.state.placeId || ""
      if (placeId) query.placeId = placeId

      const placeType = state?.state.placeType || ""
      if (placeType) query.placeType = placeType

      const propertyConfigTypeId = state?.state.propertyConfigTypeId?.toString()
      if (propertyConfigTypeId)
        query.propertyConfigTypeId = propertyConfigTypeId

      const smokeAllowed = state?.state?.smokeAllowed
      if (smokeAllowed) query.smokeAllowed = "true"

      const sortBy = state?.state.sortBy
      if (sortBy && sortBy !== "highToLow") query.sortBy = sortBy

      const startDate = state?.state.startDate
      if (startDate) query.startDate = startDate

      const toggleOn = state?.state.toggleOn
      if (toggleOn) query.toggleOn = "true"

      const isBoundaryVisible = state?.state.isBoundaryVisible
      query.isBoundaryVisible = isBoundaryVisible ? "true" : "false"

      const isBoundaryAutoRemoved = state?.state.isBoundaryAutoRemoved
      if (isBoundaryAutoRemoved) query.isBoundaryAutoRemoved = "true"

      const cancellable = state?.state.cancellable
      if (cancellable) query.cancellable = cancellable ? "true" : "false"

      const nearByProperties = state?.state.nearByProperties
      if (nearByProperties)
        query.nearByProperties = nearByProperties ? "true" : "false"

      const limit = state?.state.limit
      if (limit)
        query.limit = nearByProperties
          ? limit?.toString()
          : NO_OF_PROERTIES_PER_PAGE_IN_LISTING_PAGE?.toString()

      const queryString = Object.entries(query)
        .map(([k, v]) => `${k}=${v}`)
        .join("&")

      const newURL = `/vacation-rentals/${slugify(
        slugName.replace(/,/g, "-").replace(/ /g, "-"),
        { lower: false, replacement: "_" },
      )}?${queryString}`
      state.state.currentUpdatedURL = newURL
    },
    updateApiRequestType: (
      state,
      action: PayloadAction<{ apiRequestType: SearchApiRequestType }>,
    ) => {
      state.state.apiRequestType = action.payload.apiRequestType
    },
    setSearchQuery: (
      state,
      action: PayloadAction<{ shouldUpdateUrl?: boolean } | undefined>,
    ) => {
      const shouldURLUpdate = action?.payload?.shouldUpdateUrl

      // Removing accents and diacritical marks from location name Ex: "Córdoba" => "Cordoba"
      const locationName = normalizeText(
        state?.state.longName || state?.state.shortName || "USA",
      )
      const isChildAllowed =
        state.state?.childAllowed ||
        (!!state.state.childrenCount && state.state.childrenCount > 0)
      state.state.suitableChild = isChildAllowed
      state.state.suitableInfant = isChildAllowed
      state.state.childAllowed = isChildAllowed

      state.state.numberOfGuest =
        (state.state.adultCount || 0) + (state.state.childrenCount || 0)

      if (
        !state?.state.startDate ||
        state?.state.startDate === "null" ||
        !state?.state.endDate ||
        state?.state.endDate === "null"
      ) {
        state.state.startDate = null
        state.state.endDate = null
      }
      // state.state.nearByProperties = false
      state.searchQuery = {
        FeaturesAndAmenities:
          state?.state.FeaturesAndAmenities?.join(",") || "",
        Location: state?.state.Location?.join(",") || "",
        propertyTypeDesc: state?.state.propertyTypeDesc?.join(",") || "",
        adultCount: state?.state.adultCount || 0,
        bounds: state?.state.bounds || "",
        childAllowed: isChildAllowed,
        suitableChild: isChildAllowed,
        childrenCount: state?.state.childrenCount || 0,
        endDate:
          state?.state.startDate &&
          state?.state.startDate !== "null" &&
          state?.state.endDate &&
          state?.state.endDate !== "null"
            ? state?.state.endDate
            : "",
        eventAllowed: state?.state?.eventAllowed || false,
        partyAllowed: state?.state?.eventAllowed || false,
        isSearchingInMap: state?.state.isSearchingInMap || false,

        lat: state?.state.lat || 0,
        limit: state?.state.limit || NO_OF_PROERTIES_PER_PAGE_IN_LISTING_PAGE,
        lng: state?.state.lng || 0,
        maxPrice: state?.state?.maxprice || 0,
        minBathRoom: state?.state?.numberOfBathroom || 0,
        minBedRoom: state?.state?.numberOfBedroom || 0,
        minBed: state?.state?.minBed || 0,
        minPrice: state?.state?.minPrice || 0,
        // if boundary is visible then location name must be passed even if map is dragged.
        name: state?.state.isBoundaryVisible
          ? locationName
          : state?.state.isSearchingInMap
          ? "map area"
          : locationName,
        noOfDays: state?.state.noOfDays || 0,
        numberOfGuest: state?.state?.numberOfGuest || 0,
        page: state?.state.page || 0,
        petAllowed: state?.state.petAllowed || false,
        placeId: state?.state.placeId || "ChIJCzYy5IS16lQRQrfeQ5K5Oxw",
        placeType: state?.state.placeType || "",
        propertyConfigTypeId: state?.state.propertyConfigTypeId ?? null,

        smokeAllowed: state?.state?.smokeAllowed || false,
        sortBy: state?.state.sortBy || "highToLow",
        startDate:
          state?.state.startDate &&
          state?.state.startDate !== "null" &&
          state?.state.endDate &&
          state?.state.endDate !== "null"
            ? state?.state.startDate
            : "",
        toggleOn: state?.state.toggleOn || false,
        mapDragged: state?.state.isSearchingInMap || false,
        withPolygon: state?.state.isBoundaryVisible || false,
        cancellable: state?.state.cancellable || false,
        nearByProperties: state?.state.nearByProperties || false,
        expandedMiles: state?.state.expandedMiles || null,
      }

      if (shouldURLUpdate === false) {
        // do not update the URL at initial render of vacation-rental
        return
      } else {
        window.history.pushState({}, "", state?.state?.currentUpdatedURL)
      }
    },
    setShowClearFilterButton: (
      state,
      action: PayloadAction<{ showClearFilterButton: boolean }>,
    ) => {
      state.state.showClearFilterButton = action.payload.showClearFilterButton
    },
    setClickClearFilter: (
      state,
      action: PayloadAction<{ clickClearFilter: boolean }>,
    ) => {
      state.state.clickClearFilter = action.payload.clickClearFilter
    },
  },
})

export const {
  markSearch,
  setSearchQuery,
  resetSearch,
  updateApiRequestType,
  setShowClearFilterButton,
  setClickClearFilter,
} = searchSlice.actions

export const selectSearch = (state: RootState) => state.searchReducer.state

export const selectSearchLatLng = (state: RootState) => ({
  lat: state.searchReducer?.state.lat || 37.7749295,
  lng: state.searchReducer?.state.lng || -122.4194155,
})

export const selectSearchQuery = (state: RootState) =>
  state.searchReducer.searchQuery
export const selectWithPolygon = (state: RootState) =>
  state.searchReducer.searchQuery?.withPolygon

export const selectSearchPlaceId = (state: RootState) =>
  state.searchReducer.searchQuery?.placeId

export const selectIsMapDragged = (state: RootState) =>
  state.searchReducer.searchQuery?.mapDragged || false

export const selectShouldDisplayTotalPrice = (state: RootState) =>
  state.searchReducer?.state?.toggleOn

export const selectNearByProperties = (state: RootState) =>
  state.searchReducer?.searchQuery?.nearByProperties

export const selectIsBoundaryAutoRemoved = (state: RootState) =>
  state.searchReducer?.state?.isBoundaryAutoRemoved

export const selectApiRequestType = (state: RootState) =>
  state.searchReducer?.state?.apiRequestType

export const selectShowClearFilterButton = (state: RootState) =>
  state.searchReducer?.state?.showClearFilterButton

export const selectClickClearFilter = (state: RootState) =>
  state.searchReducer?.state?.clickClearFilter

export default searchSlice.reducer
